import { useFormContext } from 'react-hook-form';
import { useQueryHelper } from '@anytag/shared-hooks';
import { getValuesArray } from '@anytag/shared-atoms';
import { useAllAdvertisersForSearchLazyQuery } from '@anytag/graphql';
import { getSelectOptions, getValuableVariables } from '@anytag/shared-utils';
import { SelectValueType } from '../selector-field';
import { AsyncSelectorSearchField, AsyncSelectorSearchFieldProps } from '../async-selector-search-field';
import {
  AdvertiserSearchOption,
  AsyncSearchAdvertiserFieldProps,
  SingleAsyncSearchAdvertiserFieldProps,
  MultipleAsyncSearchAdvertiserFieldProps
} from './types';

// Function overloads for proper type inference based on `multiple` prop
export function AsyncSearchAdvertiserField<T extends string>(
  props: MultipleAsyncSearchAdvertiserFieldProps<T>
): React.ReactElement;
export function AsyncSearchAdvertiserField<T extends string>(
  props: SingleAsyncSearchAdvertiserFieldProps<T>
): React.ReactElement;
export function AsyncSearchAdvertiserField<T extends string>({
  name,
  multiple,
  countryId,
  searchProps,
  ...restProps
}: AsyncSearchAdvertiserFieldProps<T>): React.ReactElement {
  const { t, enqueueSnackbar } = useQueryHelper();
  const { getAllAdvertisersForSearch, loading } = useAllAdvertisersForSearchLazyQuery();
  const { watch } = useFormContext<{ [name: string]: SelectValueType<AdvertiserSearchOption> }>();

  const value = watch(name);
  const selectedAdvertisers = getValuesArray({ value, multiple }) as string[];

  const fetchAdvertisers: AsyncSelectorSearchFieldProps<T, AdvertiserSearchOption>['onSearch'] = async (keyword) => {
    let advertisers: AdvertiserSearchOption[] = [];

    try {
      const { data } = await getAllAdvertisersForSearch({
        variables: getValuableVariables({
          keyword,
          countryId,
          includeIds: selectedAdvertisers.map(Number).filter((id) => !Number.isNaN(id))
        })
      });
      advertisers = getSelectOptions([
        ...(data?.allAdvertisersForSearch.includedAdvertisers || []),
        ...(data?.allAdvertisersForSearch.advertisers || [])
      ]);
    } catch (error) {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    }

    return advertisers;
  };

  const commonProps: Pick<
    AsyncSelectorSearchFieldProps<T, AdvertiserSearchOption>,
    'name' | 'loading' | 'onSearch' | 'searchProps'
  > = {
    name,
    loading,
    onSearch: fetchAdvertisers,
    searchProps: { placeholder: t<string>('Type to search and display options'), ...searchProps }
  };

  return multiple ? (
    <AsyncSelectorSearchField<T, AdvertiserSearchOption>
      {...(restProps as Partial<MultipleAsyncSearchAdvertiserFieldProps<T>>)}
      {...commonProps}
      multiple={true}
    />
  ) : (
    <AsyncSelectorSearchField<T, AdvertiserSearchOption>
      {...(restProps as Partial<SingleAsyncSearchAdvertiserFieldProps<T>>)}
      {...commonProps}
      multiple={false}
    />
  );
}
